import React, { useState, useEffect, useRef } from 'react';
import styles from './VirtualizedList.module.css';

/**
 * Props for the VirtualizedList component
 */
interface VirtualizedListProps<T> {
  /** Array of items to render */
  items: T[];
  /** Function to render each item */
  renderItem: (item: T, index: number) => React.ReactNode;
  /** Height of each item in pixels */
  itemHeight: number;
  /** Total height of the virtualized list container */
  height: number;
  /** Width of the virtualized list container (can be percentage or pixels) */
  width: string | number;
  /** Optional className to apply to the container */
  className?: string;
  /** Optional ID for accessibility and testing */
  id?: string;
}

/**
 * A virtualized list component that only renders items visible in the viewport
 * to improve performance with large datasets.
 */
function VirtualizedList<T>({
  items,
  renderItem,
  itemHeight,
  height,
  width,
  className = '',
  id,
}: VirtualizedListProps<T>): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  
  // Handle scroll events to update which items are in view
  const handleScroll = () => {
    if (containerRef.current) {
      setScrollTop(containerRef.current.scrollTop);
    }
  };
  
  // Calculate which items should be visible
  const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight));
  const endIndex = Math.min(
    items.length - 1,
    Math.floor((scrollTop + height) / itemHeight)
  );
  
  // Add a buffer of items above and below for smoother scrolling
  const visibleStartIndex = Math.max(0, startIndex - 5);
  const visibleEndIndex = Math.min(items.length - 1, endIndex + 5);
  
  // Calculate total scroll height
  const totalHeight = items.length * itemHeight;
  
  // Items to render
  const visibleItems = [];
  for (let i = visibleStartIndex; i <= visibleEndIndex; i++) {
    visibleItems.push(
      <div 
        key={i} 
        style={{ 
          height: `${itemHeight}px`, 
          position: 'absolute', 
          top: `${i * itemHeight}px`,
          width: '100%'
        }}
      >
        {renderItem(items[i], i)}
      </div>
    );
  }
  
  return (
    <div
      id={id}
      ref={containerRef}
      className={`${styles.virtualizedList} ${className}`}
      style={{ height, width, position: 'relative', overflow: 'auto' }}
      onScroll={handleScroll}
    >
      <div style={{ height: `${totalHeight}px`, position: 'relative' }}>
        {visibleItems}
      </div>
    </div>
  );
}

// Create a properly typed memoized component
const MemoizedVirtualizedList = React.memo(
  VirtualizedList
) as typeof VirtualizedList;

export default MemoizedVirtualizedList;